import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";

// ============================================================================
// Types
// ============================================================================

export interface SleepActivityState {
  lifestyleId?: string;
  // Sleep
  minHoursOfSleep: number | null;
  maxHoursOfSleep: number | null;
  // Exercise
  isExercising: boolean | null;
  minHoursOfExercise: number | null;
  maxHoursOfExercise: number | null;
}

interface SleepActivityContextType {
  state: SleepActivityState;
  updateState: (updates: Partial<SleepActivityState>) => void;
}

// ============================================================================
// Context
// ============================================================================

const SleepActivityContext = createContext<
  SleepActivityContextType | undefined
>(undefined);

const initialState: SleepActivityState = {
  lifestyleId: undefined,
  minHoursOfSleep: null,
  maxHoursOfSleep: null,
  isExercising: null,
  minHoursOfExercise: null,
  maxHoursOfExercise: null,
};

export function SleepActivityProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const [state, setState] = useState<SleepActivityState>(initialState);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (isLoading || !patient || hydratedRef.current) return;
    hydratedRef.current = true;

    const lifestyle = (patient as any)?.lifestyle;
    if (!lifestyle) return;

    const next: Partial<SleepActivityState> = {};

    if (lifestyle.id) next.lifestyleId = lifestyle.id;

    if (lifestyle.minHoursOfSleep != null)
      next.minHoursOfSleep = lifestyle.minHoursOfSleep;
    if (lifestyle.maxHoursOfSleep != null)
      next.maxHoursOfSleep = lifestyle.maxHoursOfSleep;
    if (lifestyle.isExercising != null)
      next.isExercising = lifestyle.isExercising;
    if (lifestyle.minHoursOfExercise != null)
      next.minHoursOfExercise = lifestyle.minHoursOfExercise;
    if (lifestyle.maxHoursOfExercise != null)
      next.maxHoursOfExercise = lifestyle.maxHoursOfExercise;

    if (Object.keys(next).length > 0) {
      setState((prev) => ({ ...prev, ...next }));
    }
  }, [patient, isLoading]);

  const updateState = useCallback((updates: Partial<SleepActivityState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  return (
    <SleepActivityContext.Provider value={{ state, updateState }}>
      {children}
    </SleepActivityContext.Provider>
  );
}

export function useSleepActivity() {
  const context = useContext(SleepActivityContext);
  if (!context) {
    throw new Error(
      "useSleepActivity must be used within SleepActivityProvider",
    );
  }
  return context;
}
